import { Clock, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/stores/ui-store';
import { useTrial } from '@/hooks/use-trial';

/**
 * Floating banner shown at the top of the app when:
 * - Trial is active with <=3 days remaining (amber warning)
 * - Trial has expired with no subscription (red alert)
 *
 * Cancelled-subscription state is handled by TrialPaywallModal, not here.
 *
 * Positioned above the main content, below SaaSAuthPrompt.
 * Pattern matches the impersonation banner in app-layout.tsx.
 */
export function TrialBanner() {
  const {
    shouldShowTrialBanner,
    shouldShowExpiredBanner,
    shouldShowCancelledBanner,
    shouldShowActivationBanner,
    trialDaysRemaining,
    isSharedAccess,
  } = useTrial();
  const setView = useUIStore((s) => s.setView);

  if (
    !shouldShowTrialBanner &&
    !shouldShowExpiredBanner &&
    !shouldShowCancelledBanner &&
    !shouldShowActivationBanner
  ) {
    return null;
  }

  // Determine color and icon based on state priority
  let bgColor = 'bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500';
  let Icon = Clock;
  let message = '';
  let buttonText = 'Browse Plans';
  let bannerKind: 'trial' | 'expired' | 'cancelled' | 'activation' = 'trial';
  // For the activation case (admin), the right destination is the dedicated
  // store page where they manage subs / allocate seats. Sub-users get the
  // in-app settings (which surfaces admin contact info).
  let onButtonClick: () => void = () => setView('SETTINGS');

  if (shouldShowExpiredBanner) {
    bgColor = 'bg-gradient-to-r from-red-600 via-red-500 to-red-600';
    Icon = AlertTriangle;
    message = 'Your free trial has expired. Purchase a plan to continue.';
    bannerKind = 'expired';
  } else if (shouldShowCancelledBanner) {
    bgColor = 'bg-gradient-to-r from-red-600 via-red-500 to-red-600';
    Icon = AlertTriangle;
    message = 'Your subscription was cancelled. Purchase a plan to continue.';
    bannerKind = 'cancelled';
  } else if (shouldShowActivationBanner) {
    bgColor = 'bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600';
    Icon = AlertTriangle;
    message = isSharedAccess
      ? 'Your admin has a subscription. Please ask them to activate your license.'
      : 'You have a subscription. Please activate your license to continue.';
    buttonText = isSharedAccess ? 'View Settings' : 'Activate Now';
    bannerKind = 'activation';
    if (!isSharedAccess) {
      onButtonClick = () => { window.open('/store', '_blank', 'noopener,noreferrer'); };
    }
  } else if (shouldShowTrialBanner) {
    message = `Free trial: ${trialDaysRemaining} day${trialDaysRemaining !== 1 ? 's' : ''} remaining`;
    bannerKind = 'trial';
  }

  return (
    <div
      data-testid="trial-banner"
      data-banner-kind={bannerKind}
      className={cn('flex items-center justify-center gap-2 text-xs font-medium py-1.5 px-3 shrink-0 z-50 text-white', bgColor)}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 animate-pulse" />
      <span data-testid="trial-banner-message">{message}</span>
      <button
        data-testid="trial-banner-cta"
        onClick={onButtonClick}
        className="underline underline-offset-2 font-semibold hover:opacity-80 transition-opacity"
      >
        {buttonText}
      </button>
    </div>
  );
}
